import { useState } from 'react';
import { supabase } from '@/lib/supabase';

interface PlaintesSectionProps {
  suiteId: string;
  plaintes: any[];
  medecinId: string;
  suiteTerminee: boolean;
  onRefresh: () => void;
}

const textareaClass = 'w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-clinic-blue focus:outline-none transition-colors resize-none text-sm bg-white';
const labelClass = 'block text-sm font-semibold text-gray-700 mb-2';

export default function PlaintesSection({
  suiteId,
  plaintes,
  medecinId,
  suiteTerminee,
  onRefresh,
}: PlaintesSectionProps) {
  const [showAddPlainte, setShowAddPlainte] = useState(false);
  const [addPlainteLoading, setAddPlainteLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [plainteDesc, setPlainteDesc] = useState('');

  const handleAddPlainte = async () => {
    if (!plainteDesc.trim()) {
      setErrorMsg('Veuillez décrire la plainte et/ou les symptômes.');
      return;
    }
    setAddPlainteLoading(true);
    setErrorMsg(null);
    try {
      const { error } = await supabase.from('plaintes_suivi').insert({
        suite_soin_id: suiteId,
        description: plainteDesc.trim(),
        date_plainte: new Date().toISOString().split('T')[0],
        medecin_id: medecinId || null,
      });
      if (error) throw error;
      setPlainteDesc('');
      setShowAddPlainte(false);
      onRefresh();
    } catch (err: any) {
      setErrorMsg(err.message || "Erreur lors de l'enregistrement.");
    } finally {
      setAddPlainteLoading(false);
    }
  };

  return (
    <section className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-100 flex items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    Plaintes & symptômes
        </h2>
        <div className="flex items-center gap-2">
          {!suiteTerminee && (
            <button
              onClick={() => { setShowAddPlainte(!showAddPlainte); setErrorMsg(null); }}
              className={`px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                showAddPlainte ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' : 'bg-clinic-blue text-white hover:bg-clinic-blue-dark'
              }`}
            >
              <i className={showAddPlainte ? 'ri-close-line' : 'ri-add-line'}></i>
              {showAddPlainte ? 'Annuler' : 'Ajouter une plainte'}
            </button>
          )}
        </div>
      </div>

      <div className="p-6 space-y-5">
        {errorMsg && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center gap-2">
            <i className="ri-error-warning-line flex-shrink-0"></i>{errorMsg}
          </div>
        )}

        {showAddPlainte && (
          <div className="p-5 bg-clinic-blue/5 border border-clinic-blue/30 rounded-xl space-y-4">
            <h3 className="font-bold text-gray-900 text-sm">Nouvelle plainte / symptôme</h3>
            <div>
              <label className={labelClass}>Plainte & symptômes <span className="text-red-500">*</span></label>
              <textarea
                value={plainteDesc}
                onChange={(e) => setPlainteDesc(e.target.value)}
                className={textareaClass}
                rows={3}
                maxLength={1000}
                placeholder="Décrire la plainte du patient et les symptômes associés..."
              />
            </div>
            <button
              onClick={handleAddPlainte}
              disabled={addPlainteLoading}
              className="px-5 py-2.5 bg-clinic-blue text-white rounded-xl hover:bg-clinic-blue-dark transition-all font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              {addPlainteLoading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
              Enregistrer la plainte
            </button>
          </div>
        )}

        {plaintes.length === 0 && !showAddPlainte ? (
          <div className="text-center py-8">
            <i className="ri-file-list-3-line text-4xl text-gray-300 mb-3"></i>
            <p className="text-sm text-gray-500">Aucune plainte enregistrée pour l'instant.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {plaintes.map((p) => (
              <div key={p.id} className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="p-4 bg-gray-50/60">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-semibold text-clinic-blue-dark bg-clinic-blue/15 px-2 py-0.5 rounded-full whitespace-nowrap">
                          {new Date(p.date_plainte).toLocaleDateString('fr-FR')}
                        </span>
                        {p.medecins && (
                          <span className="text-xs text-gray-500">par Dr {p.medecins.nom} {p.medecins.prenom}</span>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-gray-900 mt-2 whitespace-pre-wrap">{p.description}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}